<template>
    <div class="kb-wrapper">
    <div class="kb-board">
        <div
            v-for="(column, colIndex) in columns"
            :key="column.id"
            class="kb-column"
            :class="{ 'kb-column--drag-over': dragOverColumn === colIndex }"
            @drop="onDrop($event, colIndex)"
            @dragover.prevent="onDragOver($event, colIndex)"
            @dragleave="onDragLeave"
        >
            <!-- Column header -->
            <div class="kb-column-header">
                <div class="kb-column-header-left">
                    <i v-if="column.icon" :class="column.icon" class="kb-column-icon"></i>
                    <span class="kb-column-title">{{ column.title }}</span>
                </div>
                <span class="kb-column-count">{{ (column.items || []).length }}</span>
            </div>

            <!-- Column body -->
            <div class="kb-column-body">
                <div
                    v-for="(item, itemIndex) in column.items"
                    :key="item.id || itemIndex"
                    class="kb-card-wrap"
                    draggable="true"
                    @dragstart="onDragStart($event, itemIndex, colIndex)"
                    @dragend="onDragEnd"
                >
                    <slot name="card" :item="item" :column="column">
                        <div class="kb-card-default">{{ item.title || item.name || item.id }}</div>
                    </slot>
                </div>

                <!-- Empty column -->
                <div v-if="!column.items || column.items.length === 0" class="kb-column-empty">
                    <i class="fal fa-inbox"></i>
                </div>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
export default {
    name: 'BmvKanban',
    props: {
        columns: {
            type: Array,
            required: true
            // [{ id, title, icon, items: [] }]
        }
    },
    data() {
        return {
            dragOverColumn: -1,
            dragSource: null
        }
    },
    methods: {
        onDragStart(evt, itemIndex, colIndex) {
            evt.dataTransfer.effectAllowed = 'move'
            evt.dataTransfer.setData('itemIndex', itemIndex)
            evt.dataTransfer.setData('colIndex', colIndex)
            this.dragSource = { itemIndex, colIndex }
            evt.currentTarget.classList.add('kb-dragging')
        },
        onDragEnd(evt) {
            evt.currentTarget.classList.remove('kb-dragging')
            this.dragOverColumn = -1
            this.dragSource = null
        },
        onDragOver(evt, colIndex) {
            evt.dataTransfer.dropEffect = 'move'
            this.dragOverColumn = colIndex
        },
        onDragLeave() {
            this.dragOverColumn = -1
        },
        onDrop(evt, targetColIndex) {
            evt.preventDefault()
            const sourceItemIndex = parseInt(evt.dataTransfer.getData('itemIndex'))
            const sourceColIndex = parseInt(evt.dataTransfer.getData('colIndex'))
            this.dragOverColumn = -1

            if (sourceColIndex === targetColIndex) return

            this.$emit('move', {
                sourceColIndex,
                sourceItemIndex,
                targetColIndex,
                item: this.columns[sourceColIndex].items[sourceItemIndex],
                targetColumn: this.columns[targetColIndex]
            })
        }
    }
}
</script>

<style scoped>
.kb-wrapper {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.kb-board {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 8px 0 16px;
    min-height: 400px;
}

.kb-column {
    flex: 1;
    min-width: 240px;
    max-width: 320px;
    background: #eef2f7;
    border-radius: 14px;
    border: 2px dashed transparent;
    box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.04);
    transition: border-color 0.2s, background 0.2s;
    display: flex;
    flex-direction: column;
}

.kb-column--drag-over {
    border-color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

/* Column header */
.kb-column-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #0891B2;
    border-radius: 12px 12px 0 0;
    margin: -1px -1px 0;
}

.kb-column-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kb-column-icon {
    color: #fff !important;
    font-size: 0.88rem;
}

.kb-column-title {
    font-size: 0.84rem;
    font-weight: 700;
    color: #fff;
}

.kb-column-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    border-radius: 8px;
    font-size: 0.72rem;
    font-weight: 700;
}

/* Column body */
.kb-column-body {
    flex: 1;
    padding: 10px 10px 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Card wrap */
.kb-card-wrap {
    cursor: grab;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.kb-card-wrap:active {
    cursor: grabbing;
}

.kb-card-wrap.kb-dragging {
    opacity: 0.4;
    transform: scale(0.95);
}

.kb-card-default {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    font-size: 0.84rem;
    color: #334155;
}

/* Empty column */
.kb-column-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    color: #cbd5e1;
    font-size: 1.5rem;
}
</style>
